import { Grid, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import DeviceBox from '../SensorBox/SensorBox';
import { useInfinitePaginationWithRef } from '../../../Devices/components/DevicesTable/hooks';
import InfiniteTable from '../../../../components/InfiniteTable/InfiniteTable';
import {
  MediaDown,
  MediaUp,
} from '../../../../components/MediaQueries/MediaQueries';
import {
  SortOptionSelectItem,
  SortState,
  useSortState,
} from '../../../../utils/sort';
import { useSensorsTableColumns } from '../../hooks';
import {
  mobileSortOptions,
  sensorsSortingReducer,
  sortSensors,
} from '../../provider';
import { MedisanteDevice, SensorsSortBy } from '../../types';
import { useSensorTableStyles } from '../../styles';

interface Props {
  sensors: MedisanteDevice[];
  totalView: boolean;
}

function SensorsTable(props: Props) {
  const { sensors: initialSensors, totalView } = props;
  const [sensors, setSensors] = useState(initialSensors);
  useEffect(() => {
    setSensors(initialSensors);
  }, [initialSensors]);
  const classes = useSensorTableStyles();

  const measurementCount = sensors.reduce(
    (sum, curr) =>
      sum +
      (totalView ? curr.measurementCount || 0 : curr.measurementTripCount || 0),
    0
  );

  const handleSortStateChange = React.useCallback(
    (sortState) => {
      const sortedDevices = sortSensors(sensors, sortState, totalView);
      setSensors(sortedDevices);
    },
    [sensors, setSensors, totalView]
  );

  const { sortState, updateSortState, currentOptionId } = useSortState<
    SortState<MedisanteDevice>,
    SensorsSortBy
  >(sensorsSortingReducer, {}, handleSortStateChange, mobileSortOptions);

  const tableColumns = useSensorsTableColumns(
    sortState,
    updateSortState,
    totalView
  );

  const [data, nextPage, hasMore, page] = useInfinitePaginationWithRef<
    MedisanteDevice,
    HTMLDivElement
  >(sensors);

  return (
    <>
      <MediaUp query="md">
        <Typography variant="h6">Sensors</Typography>
        <InfiniteTable
          page={page}
          loadMore={nextPage}
          hasMore={hasMore}
          data={data}
          tableColumns={tableColumns}
          sortState={sortState}
          getKeyValue={(d) => d.id}
        />

        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          className={classes.summary}
        >
          <Grid item className={classes.summaryItem}>
            Total device count: {sensors.length}
          </Grid>
          <Grid item className={classes.summaryItem}>
            Total measurement count: {measurementCount}
          </Grid>
        </Grid>
      </MediaUp>
      <MediaDown query="sm">
        <Typography variant="h6">Sensors</Typography>
        <MedSelect
          name="sort-option"
          placeholder="Sort by"
          variant="outlined"
          fullWidth
          options={mobileSortOptions}
          valueGetter={(o: SortOptionSelectItem<SensorsSortBy>) => o.id}
          optionTextGetter={(o: SortOptionSelectItem<SensorsSortBy>) => o.label}
          value={currentOptionId}
          onChange={(e: any) => {
            const option = mobileSortOptions.find(
              (o) => o.id === e.target.value
            );
            option && updateSortState(option.value);
          }}
        />

        <Grid container alignItems="center" className={classes.summary}>
          <Grid item className={classes.summaryItem}>
            Total device count: {sensors.length}
          </Grid>
          <Grid item className={classes.summaryItem}>
            Total measurement count: {measurementCount}
          </Grid>
        </Grid>

        <Grid container direction="column">
          {sensors.map((s) => (
            <DeviceBox totalView={totalView} device={s} key={s.id} />
          ))}
        </Grid>
      </MediaDown>
    </>
  );
}

export default SensorsTable;
